import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const THEME_KEY = 'civix_theme';

/**
 * @returns {'dark'|'light'} Stored preference. Anything but an explicit "light" is dark,
 * so the CRM never follows the OS setting on its own.
 */
export function readStoredTheme() {
  try { return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark'; } catch (_) { return 'dark'; }
}

/**
 * Mirror the theme on <html data-theme> + color-scheme and persist it.
 * @param {'dark'|'light'} theme
 */
export function applyTheme(theme) {
  try {
    const el = document.documentElement;
    el.setAttribute('data-theme', theme);
    el.style.colorScheme = theme;
  } catch (_) { /* no DOM (SSR/tests) */ }
  try { localStorage.setItem(THEME_KEY, theme); } catch (_) { /* private mode */ }
}

const ThemeContext = createContext({ theme: 'dark', setTheme: () => {}, toggleTheme: () => {} });

/**
 * Light / dark theme provider. Default is dark.
 * @param {{children: React.ReactNode}} props
 * @returns {JSX.Element}
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);

  useEffect(() => { applyTheme(theme); }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t === 'light' ? 'light' : 'dark'), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), []);
  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** @returns {{theme: 'dark'|'light', setTheme: (t: string) => void, toggleTheme: () => void}} */
export function useTheme() {
  return useContext(ThemeContext);
}
